/**
 * Sample fallback dataset for the Messaging Activity report.
 *
 * Ported from the two standalone Favor mockups (v6) so the tab never renders
 * blank when the live endpoint is unavailable (same resilience approach as
 * Channel Attribution's placeholder RAW). Shapes match the real API payloads:
 *  - SAMPLE_MEMBERS  ← getMessagingActivity `members[]`
 *  - sampleHistory() ← getMemberMessageHistory `{ member, periods, months, conversations }`
 *
 * The v6 design frames messaging by REPLIES on both sides, so the shape adds:
 *  - snap.orep    outbound repliers — distinct people she messaged who replied
 *  - snap.irep    inbound replies   — distinct inbound people she replied to
 *  - snap.outMin  median minutes to first reply on her outbound conversations
 *  - snap.inMin   median minutes for her to reply to an inbound conversation
 *  - account      approved | hidden | inactive | deactivated | suspended | deleted (ghost is never listed)
 *  - month.repliedOut / period.orep  the outbound-reply split, by month & period
 * See API_CHANGES.md for the full contract the live backend must satisfy.
 */
const RAW = [
  { id: 'diva', name: 'Diva', age: 24, gender: 'F', city: 'New Delhi', joined: '2025-06-12', verified: true, tier: 'Free tier', account: 'approved',
    snap7d: { pm: 2, ms: 3, pmh: 14, mr: 26, rr: 14, orep: 1, outMin: 90, inMin: 2880 },
    year: { inMsgs: 863, outMsgs: 112, peopleIn: 214, peopleOut: 38, replied: 47, repliedOut: 24, likes: 1940 } },
  { id: 'annie', name: 'Annie', age: 25, gender: 'F', city: 'New Delhi', joined: '2025-08-03', verified: true, tier: 'Luxury', account: 'approved',
    snap7d: { pm: 9, ms: 41, pmh: 23, mr: 58, rr: 71, orep: 6, outMin: 25, inMin: 90 },
    year: { inMsgs: 1240, outMsgs: 392, peopleIn: 300, peopleOut: 95, replied: 213, repliedOut: 60, likes: 2680 } },
  { id: 'rohan', name: 'Rohan', age: 34, gender: 'M', city: 'Mumbai', joined: '2025-09-19', verified: true, tier: 'Luxury', account: 'approved',
    snap7d: { pm: 21, ms: 73, pmh: 4, mr: 6, rr: 50, orep: 8, outMin: 150, inMin: 180 },
    year: { inMsgs: 92, outMsgs: 1180, peopleIn: 60, peopleOut: 540, replied: 30, repliedOut: 210, likes: 215 } },
  { id: 'siya', name: 'Siya', age: 19, gender: 'F', city: 'Faridabad', joined: '2025-10-28', verified: true, tier: 'Free tier', account: 'inactive',
    snap7d: { pm: 5, ms: 12, pmh: 8, mr: 15, rr: 47, orep: 2, outMin: 40, inMin: 120 },
    year: { inMsgs: 248, outMsgs: 166, peopleIn: 96, peopleOut: 60, replied: 45, repliedOut: 38, likes: 540 } },
  { id: 'aditya', name: 'Aditya', age: 41, gender: 'M', city: 'Bengaluru', joined: '2025-07-05', verified: true, tier: 'Free tier', account: 'deleted',
    snap7d: { pm: 38, ms: 119, pmh: 2, mr: 3, rr: 67, orep: 7, outMin: 340, inMin: 22 },
    year: { inMsgs: 74, outMsgs: 1620, peopleIn: 40, peopleOut: 720, replied: 27, repliedOut: 250, likes: 150 } },
];

const SHAPE = [0.04, 0.06, 0.05, 0.08, 0.11, 0.09, 0.07, 0.13, 0.1, 0.08, 0.05, 0.07, 0.05];
const SHAPE_TOT = SHAPE.reduce((a, b) => a + b, 0);
const MALE_POOL = [['Rajan', 38], ['Arjun', 45], ['Vikram', 41], ['Kabir', 39], ['Dev', 47], ['Nikhil', 36], ['Aman', 43], ['Sameer', 40]];
const FEMALE_POOL = [['Priya', 27], ['Naina', 29], ['Tara', 26], ['Riya', 24], ['Meher', 28], ['Sara', 30], ['Anaya', 25], ['Kiara', 31]];

const initialsOf = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => (w[0] || '').toUpperCase()).join('') || 'M';
const daysAgo = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));

export const SAMPLE_MEMBERS = RAW.map((r) => {
  const s = r.snap7d;
  const irep = Math.round((s.pmh * s.rr) / 100); // inbound people she replied to
  return {
    id: r.id,
    name: r.name,
    initials: initialsOf(r.name),
    age: r.age,
    gender: r.gender,
    city: r.city,
    country: 'India',
    account: r.account,
    verified: r.verified,
    tier: r.tier,
    joined: r.joined,
    joinedDaysAgo: daysAgo(r.joined),
    lastActive: new Date(Date.now() - RAW.indexOf(r) * 36e5).toISOString(),
    snap: {
      pm: s.pm, ms: s.ms, pmh: s.pmh, mr: s.mr, rr: s.rr,
      orep: s.orep, irep, outMin: s.outMin, inMin: s.inMin,
      replied: irep, reached: s.pmh,
      likes: Math.round(r.year.likes * 0.02),
    },
  };
});

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthsOf(r) {
  const now = new Date();
  const out = [];
  for (let i = 12; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const w = SHAPE[12 - i] / SHAPE_TOT;
    const peopleIn = Math.max(1, Math.round(r.year.peopleIn * w));
    const peopleOut = Math.max(0, Math.round(r.year.peopleOut * w));
    const replied = Math.round(r.year.replied * w);
    const repliedOut = Math.min(peopleOut, Math.round(r.year.repliedOut * w));
    out.push({
      y: d.getFullYear(), m: d.getMonth() + 1, label: MONTH_LABELS[d.getMonth()],
      outMsgs: Math.round(r.year.outMsgs * w), inMsgs: Math.round(r.year.inMsgs * w),
      peopleOut, peopleIn,
      inboundThreads: peopleIn, replied, repliedOut,
      rr: peopleIn ? Math.round((replied / peopleIn) * 100) : 0,
      likes: Math.round(r.year.likes * w),
    });
  }
  return out;
}

// Trailing-month count each period highlights / aggregates. 7d is a live snapshot.
const PERIOD_MONTHS = { '7d': 0, '1mo': 1, '3mo': 3, '6mo': 6, '12mo': 12 };

function periodsOf(r, months) {
  const res = {};
  Object.entries(PERIOD_MONTHS).forEach(([p, n]) => {
    if (p === '7d') {
      const s = r.snap7d;
      const irep = Math.round((s.pmh * s.rr) / 100);
      res[p] = { pm: s.pm, ms: s.ms, pmh: s.pmh, mr: s.mr, orep: s.orep, replied: irep, reached: s.pmh, rr: s.rr, likes: Math.round(r.year.likes * 0.02), inboundThreads: s.pmh };
      return;
    }
    const slice = months.slice(Math.max(0, months.length - n));
    const sum = (k) => slice.reduce((a, b) => a + b[k], 0);
    const pmh = sum('peopleIn');
    const replied = sum('replied');
    res[p] = { pm: sum('peopleOut'), ms: sum('outMsgs'), pmh, mr: sum('inMsgs'), orep: sum('repliedOut'), replied, reached: pmh, rr: pmh ? Math.round((replied / pmh) * 100) : 0, likes: Math.round(r.year.likes * (n / 13)), inboundThreads: pmh };
  });
  return res;
}

function conversationsOf(r) {
  const pool = r.gender === 'F' ? MALE_POOL : FEMALE_POOL;
  const list = [];
  const outN = r.year.peopleOut > 0 ? 6 : 0;
  for (let i = 0; i < outN; i++) {
    const [nm, age] = pool[i % pool.length];
    list.push({ id: `o${i}`, name: nm, initials: initialsOf(nm), gender: r.gender === 'F' ? 'M' : 'F', age, isBot: false, dir: 'out', msgs: [12, 6, 4, 3, 2, 1][i] || 2, sent: [8, 4, 2, 2, 1, 1][i] || 1, received: [4, 2, 2, 1, 1, 0][i] || 1, replied: i < 4, lastTs: new Date(Date.now() - i * 5 * 864e5).toISOString(), firstTs: new Date(Date.now() - (i + 6) * 30 * 864e5).toISOString() });
  }
  for (let i = 0; i < 8; i++) {
    const [nm, age] = pool[(i + 3) % pool.length];
    list.push({ id: `i${i}`, name: nm, initials: initialsOf(nm), gender: r.gender === 'F' ? 'M' : 'F', age, isBot: false, dir: 'in', msgs: [9, 5, 3, 2, 1, 1, 1, 1][i] || 1, sent: i > 4 ? 0 : 1, received: [9, 4, 2, 1, 1, 1, 1, 1][i] || 1, replied: i <= 4, lastTs: new Date(Date.now() - (i + 1) * 4 * 864e5).toISOString(), firstTs: new Date(Date.now() - (i + 8) * 22 * 864e5).toISOString() });
  }
  return list;
}

export function sampleHistory(id) {
  const r = RAW.find((x) => x.id === id) || RAW[0];
  const months = monthsOf(r);
  const periods = periodsOf(r, months);
  return {
    now: new Date().toISOString(),
    member: {
      id: r.id, name: r.name, initials: initialsOf(r.name), age: r.age, gender: r.gender,
      city: r.city, country: 'India', account: r.account, verified: r.verified, tier: r.tier,
      joined: r.joined, joinedDaysAgo: daysAgo(r.joined),
      lastActive: new Date(Date.now() - 2 * 36e5).toISOString(), likesTotal: r.year.likes,
      med: { inMin: r.snap7d.inMin, outMin: r.snap7d.outMin },
    },
    periods,
    months,
    conversations: conversationsOf(r),
  };
}
